import React, { useState } from "react";
import { RiskStatistics, SafeAssessment } from "@sscs/report/ui";
import { convertFinalCheckLabel } from "@sscs/report/utils";
import { ReportDetails } from "@sscs/types";
import {
  CheckLabel,
  DataTable,
  LongTextLabel,
  Table,
  ValueOrPlaceholder,
  VerticalDivider,
} from "@sscs/ui";
import { getReleaseDateDisplay } from "@sscs/utils";
import { ApprovalStatus, LinkToReport } from "Components";
import { InsightType, InsightsVersion } from "../../../../types";
import Components from "./Components";
import Levels from "./Levels";
import styles from "./version.module.scss";

type VersionProps = {
  issueId: string;
  issueType: InsightType;
  version: InsightsVersion;
  rowIndex: number;
  isFocused: boolean;
  onFocusChange: () => void;
};

const Version = ({
  issueId,
  issueType,
  version,
  rowIndex,
  isFocused,
  onFocusChange,
}: VersionProps): React.JSX.Element => {
  const [isExpanded, setIsExpanded] = useState(false);
  const { status, scanLevel, isRepro, isDiff } = convertFinalCheckLabel(
    version.scan_report.checks,
  );
  const reportDetails: ReportDetails = {
    projectName: version.project_name,
    packageName: version.package_name,
    version: version.software_info.version,
    isRepro: isRepro,
  };

  return (
    <DataTable.ExpandedRow
      expanded={isExpanded}
      focused={isFocused}
      onChange={setIsExpanded}
      onClick={onFocusChange}
      className={isExpanded ? styles["sticky-expanded-row"] : undefined}
    >
      <DataTable.ExpandedRow.Summary>
        <Table.Cell>
          <CheckLabel
            status={status}
            scanLevel={scanLevel}
            isLevelCustomized={version.scan_report.inhibitors.customized}
            isReproducibilityCheck={isRepro}
            isDiffCheck={isDiff}
          />
        </Table.Cell>
        <Table.Cell>
          <ApprovalStatus
            status={version.approval_status}
            info={version.approval_information}
          />
        </Table.Cell>
        <Table.Cell>
          <LinkToReport id={`version-${rowIndex}`} report={reportDetails}>
            <LongTextLabel label={version.purl} className={styles.purl} />
          </LinkToReport>
        </Table.Cell>
        <Table.Cell>
          <RiskStatistics
            risk={version.scan_report.assessments}
            riskCountWrapper={(assessment, category, children) => (
              <LinkToReport
                id={`version-${category}-link-${rowIndex}`}
                report={{
                  ...reportDetails,
                  riskCategory: category,
                  riskViolations: assessment.violations,
                }}
              >
                {children}
              </LinkToReport>
            )}
            viewReportWrapper={(children) => (
              <LinkToReport
                id={`version-report-link-${rowIndex}`}
                report={reportDetails}
              >
                {children}
              </LinkToReport>
            )}
          />
        </Table.Cell>
        <Table.Cell>
          <ValueOrPlaceholder
            value={
              version.is_released
                ? getReleaseDateDisplay(version.software_info.release_date)
                : null
            }
          />
        </Table.Cell>
      </DataTable.ExpandedRow.Summary>
      <DataTable.ExpandedRow.Details className={styles.details}>
        <div className={styles["common-container"]}>
          <div className={styles["long-label-wrapper"]}>
            <LongTextLabel
              label={`${version.software_info.product} ${version.software_info.version}`}
            />
          </div>
          <VerticalDivider />
          <span>Publisher:&nbsp;</span>
          <div className={styles["long-label-wrapper"]}>
            <LongTextLabel label={version.software_info.publisher} />
          </div>
          <VerticalDivider />
          <span>Platform:&nbsp;</span>
          <strong>{version.software_info.platform}</strong>
          <VerticalDivider />
          <span>Category:&nbsp;</span>
          <strong>{version.software_info.category}</strong>
          <VerticalDivider />
          <span>License:&nbsp;</span>
          <div className={styles["license"]}>
            <LongTextLabel label={version.software_info.license} />
          </div>
        </div>
        <SafeAssessment.Panel
          disabledPoliciesCount={version.disabled_policies_count}
        >
          <SafeAssessment.Assessment
            risk={version.scan_report.assessments}
            risksCountWrapper={(risk, riskCategory, children) => (
              <LinkToReport
                id={`${riskCategory}-${rowIndex}`}
                report={{
                  projectName: version.project_name,
                  packageName: version.package_name,
                  version: version.software_info.version,
                  isRepro: isRepro,
                  riskCategory: riskCategory,
                  riskViolations: risk.violations,
                }}
              >
                {children}
              </LinkToReport>
            )}
          />
        </SafeAssessment.Panel>
        {(scanLevel ?? 0) > 0 && !isRepro && (
          <Levels
            inhibitors={version.scan_report.inhibitors}
            checks={version.scan_report.checks}
          />
        )}
        <Components
          issueId={issueId}
          issueType={issueType}
          version={version}
          rowIndex={rowIndex}
        />
      </DataTable.ExpandedRow.Details>
    </DataTable.ExpandedRow>
  );
};

export default Version;
